import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useModal } from 'context/Modal/Context';
import { useToaster } from 'context/Toaster/ToasterContext';
import { useUserAddresses } from 'hooks/queries/address';
import useApi from 'hooks/useApi';
import { isValidIBAN } from 'ibantools';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VARIANT_COLOR_ENUM } from 'types/utils';
import marketPlaceFactory from 'utils/factories/marketplace';
import * as yup from 'yup';
import { BankType } from '../types';

const schemaIfBankType = (type: BankType, schema: yup.Schema) =>
  yup.string().when('type', { is: type, then: () => schema });

// see https://mangopay.com/docs/endpoints/payouts#bank-account-object

const bankAccountSchema = () =>
  yup.object({
    type: yup.mixed<'IBAN' | 'US' | 'OTHER'>().oneOf(['IBAN', 'US', 'OTHER']).required(),
    ownerLastName: yup.string().required(),
    ownerFirstName: yup.string().required(),

    IBAN: schemaIfBankType(
      'IBAN',
      yup
        .string()
        .required()
        .test('is-valid-iban', 'bank-account-iban-invalid', (value) => isValidIBAN(value.replace(/\s/g, ''))),
    ),

    BIC: yup.string().when('type', {
      is: (v: string) => ['IBAN', 'OTHER'].includes(v),
      then: () => yup.string().required().min(8).max(11),
    }),

    accountNumber: yup.string().when('type', {
      is: (value: string) => value !== 'IBAN',
      then: () => yup.string().required(),
    }),

    ABA: schemaIfBankType('US', yup.string().required().length(9)),
    address: yup.string().required(),
  });

type BankAccountFormValues = yup.InferType<ReturnType<typeof bankAccountSchema>>;

const useBankAccountForm = () => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const { t } = useTranslation();
  const { addToaster } = useToaster();
  const { closeModal } = useModal();
  const formMethods = useForm<BankAccountFormValues>({
    resolver: yupResolver(bankAccountSchema()),
  });

  const { handleSubmit } = formMethods;
  const {
    mutate: createBankAccount,
    isPending,
    isError,
  } = useMutation({
    mutationKey: ['createBankAccount'],
    mutationFn: async (data: BankAccountFormValues) =>
      apiClient.payments.apiPaymentsbankAccountPost({
        ...data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketPlaceFactory.bankAccount() });
      addToaster({
        variant: VARIANT_COLOR_ENUM.SUCCESS,
        message: t('bank-account-create-success'),
      });
      closeModal();
    },
  });
  const { defaultBillingAddress } = useUserAddresses();

  useEffect(() => {
    if (defaultBillingAddress && defaultBillingAddress['@id']) {
      formMethods.setValue('address', defaultBillingAddress['@id']);
    }
  }, [defaultBillingAddress]);

  const onSubmit = (data: BankAccountFormValues) =>
    createBankAccount({ ...data, IBAN: data.IBAN ? data.IBAN.replace(/\s/g, '') : undefined });

  return {
    onSubmit: handleSubmit(onSubmit),
    formMethods,
    isPending,
    isError,
  };
};

export default useBankAccountForm;
